const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Date label for a lesson submission or delivery.
 *
 *   under 1 min    "just now"
 *   under 1 hour   "12m ago"
 *   under 24 hours "2h ago"
 *   yesterday      "Yesterday"
 *   2 to 6 days    "Wednesday"
 *   7 days or more "Aug 30"
 *
 * The first three branches replaced a flat "Today", which told a coach nothing
 * about whether a player had been waiting one hour or twenty — the difference
 * between comfortable and late. They are elapsed-time, not calendar: a lesson
 * submitted at 11pm reads "2h ago" at 1am rather than "Yesterday", because at
 * that distance how long ago is the fact, not which day.
 *
 * Extended here rather than written again. This is the one formatter every
 * lesson date on both sides already goes through, so the coach's list, the
 * player's list, both identity rows and both program screens picked the change
 * up without touching a call site, and cannot disagree with each other.
 *
 * Not `formatInboxTimestamp` from `messageSessions.ts`, which looks similar and
 * agrees on the middle three branches. It differs where it matters: a message
 * sent earlier today reads as its clock time ("10:24 AM"), because in a message
 * list the time of day is the useful fact. For a lesson it is not — "Today" is.
 * Reusing it would have changed every Inbox row.
 *
 * Comparison is by calendar day, not elapsed milliseconds, so a lesson
 * delivered at 11pm still reads "Yesterday" at 1am rather than "Today".
 * Sidesteps DST edges for the same reason.
 *
 * `now` is injectable for deterministic testing.
 */
export function formatLessonDate(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const elapsedMs = now.getTime() - date.getTime();

  // Future dates — clock skew, a serverTimestamp not yet settled — collapse to
  // "just now" rather than reading as a negative age.
  if (elapsedMs < 60_000) return 'just now';
  if (elapsedMs < HOUR_MS) return `${Math.floor(elapsedMs / 60_000)}m ago`;
  if (elapsedMs < DAY_MS) return `${Math.floor(elapsedMs / HOUR_MS)}h ago`;

  const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.floor((dayStart(now) - dayStart(date)) / DAY_MS);

  /* Past 24 hours the calendar takes over, so DST edges do not shift a label.
     "Today" survives only for something over 24 hours old on the same calendar
     day, which a clock change can produce. */
  if (dayDiff <= 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Absolute "Aug 30", for places a relative label would read wrong.
 *
 * A range is the case: "Aug 1 – Today" mixes two frames of reference and reads
 * as a mistake, where "Aug 1 – Sep 4" is a span. Same for anything that has to
 * stay true when read back later.
 */
export function formatAbsoluteDate(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
